import { useCallback, useRef } from "react";
import { container } from "tsyringe";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import CommentCreateRequestResource from "common/resources/Project/Config/Forum/Comment/CommentRequestResource";
import CommentResponseResource from "common/resources/Project/Config/Forum/Comment/CommentResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import { TextArea, TextAreaRef } from "components/materials/Form/TextAreaElement";
import I18n from "components/materials/I18n";
import CommentService from "services/projects/CommentService";
import { usePostId } from "hooks/usePostId";
import { useProjectId } from "hooks/useProjectId";
import classes from "./classes.module.scss";

const commentService = container.resolve(CommentService);

type IProps = {
	onCreated: (comment: CommentResponseResource) => void;
};

export default function CommentForm(props: IProps) {
	const { projectId } = useProjectId();
	const { postId } = usePostId();
	const { clearErrors, errors, handleError } = useForm();
	const textRef = useRef<TextAreaRef>(null);

	const onSubmit = useCallback(
		async (formData: Record<string, unknown>) => {
			clearErrors();
			textRef.current?.clear();

			const commentRequestResource = CommentCreateRequestResource.hydrate<CommentCreateRequestResource>({
				text: formData["text"] as string,
			});

			commentRequestResource
				.validateOrReject()
				.then((resource) => commentService.create(projectId, postId, resource))
				.then(props.onCreated)
				.catch(handleError);
		},
		[clearErrors, handleError, postId, projectId, props.onCreated],
	);

	return (
		<div className={classes["root"]}>
			<Typography type="p" weight="bold" size="large">
				{I18n.trslt(I18n.asset.pages.post.comment.default_subtitle)}
			</Typography>

			<Form onSubmit={onSubmit} className={classes["form"]} errors={errors}>
				<TextArea ref={textRef} name="text" inputClassName={classes["textarea"]} placeholder={I18n.trslt(I18n.asset.pages.post.comment.share)} />

				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} className={classes["submit-button"]} startIcon={<PaperAirplaneIcon />} type="submit">
					{I18n.trslt(I18n.asset.pages.post.comment.submit)}
				</Button>
			</Form>
		</div>
	);
}
